<script setup lang="ts">
  import { Checkbox, SearchNoResults } from '@/shared/ui';
  import type { NoticeListItem } from '@/entities/ReminderHistory';
  import type { OrderOptions } from '@/shared/lib';
  import type { NoticeSortField, NoticeSortOrder } from '../model/types';
  import ReminderHistoryRow from './ReminderHistoryRow.vue';
  import ReminderHistoryTableHeader from './ReminderHistoryTableHeader.vue';
  import ReminderHistoryRecipientRows from './ReminderHistoryRecipientRows.vue';

  interface Props {
    notices: NoticeListItem[];
    isLoading: boolean;
    showGeneralColumns: boolean;
    expandedNoticeId: number | null;
    expandedRecipients: Array<{ memberId: number; userId: number; name: string; acknowledged: boolean }>;
    isRecipientsLoading: boolean;
    recipientErrorMessage: string | null;
    mapDate: (value: string | null | undefined) => string;
    orderOptions: OrderOptions[];
    processingNoticeId?: number | null;
    currentSort?: NoticeSortField | '';
    currentOrder?: NoticeSortOrder | '';
    isFilterActive?: boolean;
    allSelected?: boolean;
    hasSelection?: boolean;
    isSelected?: (id: number) => boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    allSelected: false,
    hasSelection: false,
    isSelected: undefined,
    processingNoticeId: null,
    currentSort: undefined,
    currentOrder: undefined,
    isFilterActive: false,
  });

  const emit = defineEmits<{
    (e: 'update:sort', sort: NoticeSortField | '', order: NoticeSortOrder | ''): void;
    (e: 'filter-clicked'): void;
    (e: 'open-recipients', noticeId: number): void;
    (e: 'toggle-all'): void;
    (e: 'toggle-selection', noticeId: number): void;
    (e: 'delete-selected'): void;
    (e: 'acknowledge', noticeId: number): void;
    (e: 'delete', noticeId: number): void;
  }>();
</script>

<template>
  <div class="border border-gray-02 rounded-2xl bg-white-01">
    <ReminderHistoryTableHeader
      :show-general-columns="showGeneralColumns"
      :order-options="orderOptions"
      :current-sort="currentSort"
      :current-order="currentOrder"
      :is-filter-active="isFilterActive"
      :has-selection="hasSelection"
      @update:sort="
        (sort, order) =>
          emit('update:sort', sort as NoticeSortField | '', order as NoticeSortOrder | '')
      "
      @filter-clicked="emit('filter-clicked')"
      @delete-selected="emit('delete-selected')"
    />

    <div class="overflow-auto">
      <table class="w-full min-w-315 table-fixed">
        <colgroup>
          <col
            v-if="showGeneralColumns"
            class="w-[60px]"
          >
          <col class="w-[276px]">
          <col class="w-[120px]">
          <col class="w-[175px]">
          <col class="w-[180px]">
          <col
            v-if="showGeneralColumns"
            class="w-[132px]"
          >
          <col
            v-if="showGeneralColumns"
            class="w-[72px]"
          >
          <col class="w-[120px]">
          <col class="w-[128px]">
          <col :class="showGeneralColumns ? 'w-[124px]' : 'w-[140px]'">
        </colgroup>
        <thead class="bg-white-01 text-gray-07 border-b border-gray-02">
          <tr>
            <th
              v-if="showGeneralColumns"
              class="align-middle text-center pl-6 w-15"
            >
              <Checkbox
                id="select-all-notices"
                :checked="allSelected"
                @toggle="emit('toggle-all')"
              />
            </th>
            <th class="text-left px-4 py-4 text-xs font-semibold leading-5">
              Avisos
            </th>
            <th class="text-left px-4 py-4 text-xs font-semibold leading-5 whitespace-nowrap">
              Data de envio
            </th>
            <th class="text-left px-4 py-4 text-xs font-semibold leading-5 whitespace-nowrap">
              Colaborador remetente
            </th>
            <th class="text-left px-4 py-4 text-xs font-semibold leading-5 whitespace-nowrap">
              Cargo
            </th>
            <th
              v-if="showGeneralColumns"
              class="text-left px-4 py-4 text-xs font-semibold leading-5 whitespace-nowrap"
            >
              Enviado para
            </th>
            <th
              v-if="showGeneralColumns"
              class="text-left px-4 py-4 text-xs font-semibold leading-5 whitespace-nowrap"
            >
              Ciente
            </th>
            <th class="text-left px-4 py-4 text-xs font-semibold leading-5 whitespace-nowrap">
              Categoria
            </th>
            <th class="text-left px-4 py-4 text-xs font-semibold leading-5 whitespace-nowrap">
              Status
            </th>
            <th class="text-left px-4 py-4 text-xs font-semibold leading-5 whitespace-nowrap">
              Ações
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td
              :colspan="showGeneralColumns ? 10 : 7"
              class="px-4 py-8 text-center text-gray-04"
            >
              Carregando avisos...
            </td>
          </tr>

          <tr v-else-if="notices.length === 0">
            <td
              :colspan="showGeneralColumns ? 10 : 7"
              class="px-4 py-10"
            >
              <SearchNoResults />
            </td>
          </tr>

          <template v-else>
            <template
              v-for="notice in notices"
              :key="notice.id"
            >
              <ReminderHistoryRow
                :notice="notice"
                :show-general-columns="showGeneralColumns"
                :map-date="mapDate"
                :is-expanded="showGeneralColumns && expandedNoticeId === notice.id"
                :is-selected="props.isSelected?.(notice.id) ?? false"
                :is-acknowledge-pending="props.processingNoticeId === notice.id"
                @open-recipients="(noticeId) => emit('open-recipients', noticeId)"
                @toggle-selection="(noticeId) => emit('toggle-selection', noticeId)"
                @acknowledge="(noticeId) => emit('acknowledge', noticeId)"
                @delete="(noticeId) => emit('delete', noticeId)"
              />

              <ReminderHistoryRecipientRows
                v-if="showGeneralColumns && expandedNoticeId === notice.id"
                :notice-id="notice.id"
                :is-loading="isRecipientsLoading"
                :error-message="recipientErrorMessage"
                :recipients="expandedRecipients"
              />
            </template>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
